import { useContext, useEffect, useRef } from 'react';
import { Accordion } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { HistoryDataContext } from '../../context/HistoryContext';
import { ChatHistoryDisplay } from './ChatHistoryDisplay';
import { CustomAccordionHeader } from './CustomAccordionHeader';
import { getActiveChatHistoryRecord } from '../../services/history';
import useElysiaStore from '@/app/elysia-store';
import { getSources } from '@/services/content';

export const ChatHistoryComponent = (props) => {
  const chatHistory = useContext(HistoryDataContext);
  const { pathSession_id, setPathSession_id } = props;

  const backToChatSession = useElysiaStore((state) => state.backToChatSession);
  const sessionPath = useElysiaStore((state) => state.sessionPath);
  const allowedCollections = useElysiaStore((state) => state.allowedCollections);
  const isNewSession = useElysiaStore((state) => state.isNewSession);
  const isPrivateChat = useElysiaStore((state) => state.isPrivateChat);
  const setIsChatActive = useElysiaStore.getState().setIsChatActive;
  const setNewChat = useElysiaStore.getState().setNewChat;
  const setChatSessionId = useElysiaStore.getState().setChatSessionId;
  const setClearState = useElysiaStore.getState().setClearState;
  const setChatAgent = useElysiaStore.getState().setChatAgent;
  const setActiveChatHistoryRecord = useElysiaStore.getState().setActiveChatHistoryRecord;
  const setSources = useElysiaStore.getState().setSources;
  const setSelectedSources = useElysiaStore.getState().setSelectedSources;
  const setContext = useElysiaStore.getState().setContext;
  const setCollectionName = useElysiaStore.getState().setCollectionName;
  const setBackToChatSession = useElysiaStore.getState().setBackToChatSession;
  const setCollectionType = useElysiaStore.getState().setCollectionType;
  const setCollectionProductLine = useElysiaStore.getState().setCollectionProductLine;
  const setCollection = useElysiaStore.getState().setCollection;
  const setIsActiveChatHistoryLoading = useElysiaStore.getState().setIsActiveChatHistoryLoading;
  const setSessionPath = useElysiaStore.getState().setSessionPath;
  const setIsRestrictedChatSessionStatus =
    useElysiaStore.getState().setIsRestrictedChatSessionStatus;
  const setShowScrollIcon = useElysiaStore.getState().setShowScrollIcon;
  const setUploadedFileTitle = useElysiaStore.getState().setUploadedFileTitle;
  const setUploadedFile = useElysiaStore.getState().setUploadedFile;
  const setShowFileToasts = useElysiaStore.getState().setShowFileToasts;
  const setHistoryCollectionName = useElysiaStore.getState().setHistoryCollectionName;
  const setDataAgentDocs = useElysiaStore.getState().setDataAgentDocs;
  const setBrandDoc = useElysiaStore.getState().setBrandDoc;
  const resetAIApp = useElysiaStore.getState().resetAIApp;
  const setIsAppFileAttached = useElysiaStore.getState().setIsAppFileAttached;
  const setAppFileBadgeTitle = useElysiaStore.getState().setAppFileBadgeTitle;
  const setAvailablePrivateDocs = useElysiaStore.getState().setAvailablePrivateDocs;
  const availablePrivateDocs = useElysiaStore((state) => state.availablePrivateDocs);
  const navigate = useNavigate();
  const todayAccordionRef = useRef<HTMLDivElement>(null);
  const prevTodayLengthRef = useRef<number | null>(null);

  // added useEffect to expand and scroll to the today's bucket while adding a new chat to the bucket.
  useEffect(() => {
    if (
      prevTodayLengthRef.current !== null &&
      chatHistory?.today.length > prevTodayLengthRef.current
    ) {
      if (todayAccordionRef.current) {
        todayAccordionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      const headerId = 'todayAccordion';
      const header = document.getElementById(headerId);
      const button = header?.querySelector('button');
      if (button?.className === 'accordion-button collapsed') {
        button?.click();
      }
    }
    prevTodayLengthRef.current = chatHistory?.today.length;
  }, [chatHistory?.today.length]);

  useEffect(() => {
    if (pathSession_id !== '' && (!isNewSession || (sessionPath !== '' && backToChatSession))) {
      fetchHistoryRecord(pathSession_id);
      setBackToChatSession?.(false);
    }
    //eslint-disable-next-line
  }, [pathSession_id]);

  const startNewTopicChat = () => {
    setShowScrollIcon?.(false);
    setClearState?.(true);
    setNewChat?.(false);
    setChatAgent?.('Generic');
    setUploadedFileTitle?.('');
    setUploadedFile?.(null);
    setSources?.([]);
    setSelectedSources?.([]);
    setShowFileToasts?.(false);
    resetAIApp();
  };

  const handleActiveChatHistoryLoad = async (session_id, responseData) => {
    navigate(`/session/${session_id}`);
    setSessionPath?.(`/session/${session_id}`);
    const metadata = responseData?.metadata;
    const historyData = responseData?.history;

    try {
      const [documentResData] = await Promise.all([getSources('true', '', '', 'desc', '', '', '')]);
      if (!availablePrivateDocs) return;
      const extractTitles = (data: any[] | undefined) =>
        (data ?? []).map((doc: any) => doc?.title?.toString() ?? '').filter(Boolean);
      const documentTitles = extractTitles(documentResData?.data);
      console.log(documentResData);
      setAvailablePrivateDocs?.([...availablePrivateDocs, ...documentTitles]);
    } catch (error) {
      console.error('Error fetching sources:', error);
    }

    if (metadata?.show_attachment && metadata?.agent_name == 'Data Agent') {
      const res = await getSources('true', '', '', 'desc', '', '', 'data-agent');
      if (res) {
        // set available private docs to the list of titles returned by getSources
        setDataAgentDocs?.(res?.data?.map((doc: any) => doc?.title?.toString() || ''));
      }
    }
    if (metadata && metadata.chat_agent === 'Document') {
      setHistoryCollectionName?.(metadata?.collection_name.toString());
      const collectionName = metadata.display_name;
      if (metadata?.agent_id.length) {
        setBrandDoc(true);
      }
      if (metadata?.agent_name !== 'Data Agent') {
        setChatAgent?.('Document');
      }

      if (metadata.document_source.length) {
        if (metadata.show_attachment) {
          setIsAppFileAttached(true);
          setAppFileBadgeTitle(historyData[0]?.references[0]?.title);
        }
        setSources?.(metadata.document_source);
        setSelectedSources?.(metadata.document_source);
      } else {
        setSources?.([]);
        setSelectedSources?.([]);
      }
      if (metadata.context) {
        const filteredCollection =
          allowedCollections?.filter((obj) => {
            return obj.cname === collectionName && obj.perm === 'filter';
          }) || [];
        setCollectionName?.(collectionName);
        if (filteredCollection[0]) {
          setContext?.(filteredCollection[0].ckey);
          setCollection?.(filteredCollection[0].ckey);
          setCollectionType?.(filteredCollection[0].ctype);
          setCollectionProductLine?.(filteredCollection[0].cline);
        }
      } else {
        setCollectionName?.('My Private Collection');
        setCollectionType?.('private');
        setCollection?.('myContentsOnly');
        setContext?.('myContentsOnly');
      }
    } else if (metadata && metadata?.agent_id?.length > 0) {
      setChatAgent?.('app-agent');
    } else {
      setChatAgent?.('Generic');
      resetAIApp();
    }
    setActiveChatHistoryRecord(responseData);
    setChatSessionId?.(responseData?.session_id);
  };

  const fetchHistoryRecord = (session_id) => {
    startNewTopicChat();
    let response;
    setIsActiveChatHistoryLoading?.(true);
    (async () => {
      try {
        response = await getActiveChatHistoryRecord(session_id);
        if (response.status === 403 || response.status === 404) {
          setIsRestrictedChatSessionStatus?.(response.status);
        } else if (response[0]?.history) {
          setIsRestrictedChatSessionStatus?.('');
          handleActiveChatHistoryLoad(session_id, response[0]);
          setIsChatActive?.(true);
        }
      } catch (err) {
        console.log('error fetching chat record', err);
      } finally {
        setIsActiveChatHistoryLoading?.(false);
      }
    })();
  };

  return (
    <Accordion className="custom-accordion" defaultActiveKey={['0', '4']} alwaysOpen>
      {chatHistory?.today.length > 0 && (
        <Accordion.Item eventKey="0" ref={todayAccordionRef}>
          <CustomAccordionHeader
            disabled={isPrivateChat ? true : false}
            isOpen={true}
            eventKey="0"
            testId="today-accordion-header"
            id="todayAccordion"
          >
            <div className="custom-accordion-header-text">Today</div>
          </CustomAccordionHeader>
          <Accordion.Body className="custom-accordion-body">
            {chatHistory.today.map((chat, index) => (
              <ChatHistoryDisplay
                data={chat}
                key={index}
                fetchHistoryRecord={() => {
                  setPathSession_id(chat.session_id);
                }}
              />
            ))}
          </Accordion.Body>
        </Accordion.Item>
      )}

      {chatHistory?.yesterday.length > 0 && (
        <Accordion.Item eventKey="1">
          <CustomAccordionHeader
            disabled={isPrivateChat ? true : false}
            eventKey="1"
            testId="yesterday-accordion-header"
          >
            <div className="custom-accordion-header-text">Yesterday</div>
          </CustomAccordionHeader>
          <Accordion.Body className="custom-accordion-body">
            {chatHistory.yesterday.map((chat, index) => (
              <ChatHistoryDisplay
                data={chat}
                key={index}
                fetchHistoryRecord={() => {
                  setPathSession_id(chat.session_id);
                }}
              />
            ))}
          </Accordion.Body>
        </Accordion.Item>
      )}

      {chatHistory?.last_7_days.length > 0 && (
        <Accordion.Item eventKey="2">
          <CustomAccordionHeader
            disabled={isPrivateChat ? true : false}
            eventKey="2"
            testId="last-7-days-accordion-header"
          >
            <div className="custom-accordion-header-text">Last 7 days</div>
          </CustomAccordionHeader>
          <Accordion.Body className="custom-accordion-body">
            {chatHistory.last_7_days.map((chat, index) => (
              <ChatHistoryDisplay
                data={chat}
                key={index}
                fetchHistoryRecord={() => {
                  setPathSession_id(chat.session_id);
                }}
              />
            ))}
          </Accordion.Body>
        </Accordion.Item>
      )}

      {chatHistory?.last_30_days.length > 0 && (
        <Accordion.Item eventKey="3">
          <CustomAccordionHeader
            disabled={isPrivateChat ? true : false}
            eventKey="3"
            testId="last-30-days-accordion-header"
          >
            <div className="custom-accordion-header-text">Last 30 days</div>
          </CustomAccordionHeader>
          <Accordion.Body className="custom-accordion-body">
            {chatHistory.last_30_days.map((chat, index) => (
              <ChatHistoryDisplay
                data={chat}
                key={index}
                fetchHistoryRecord={() => {
                  setPathSession_id(chat.session_id);
                }}
              />
            ))}
          </Accordion.Body>
        </Accordion.Item>
      )}

      {chatHistory?.archived_chats.length > 0 && (
        <Accordion.Item eventKey="4">
          <CustomAccordionHeader
            disabled={isPrivateChat ? true : false}
            isOpen={true}
            eventKey="4"
            testId="archive-accordion-header"
          >
            <div className="custom-accordion-header-text">Archived Chats</div>
          </CustomAccordionHeader>
          <Accordion.Body className="custom-accordion-body">
            {chatHistory.archived_chats.map((chat, index) => (
              <ChatHistoryDisplay
                data={chat}
                key={index}
                fetchHistoryRecord={() => {
                  setPathSession_id(chat.session_id);
                }}
              />
            ))}
          </Accordion.Body>
        </Accordion.Item>
      )}
    </Accordion>
  );
};
